import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { attendanceToday, calendarDays } from '../utils/attendanceCalendar';
import './AttendanceCalendarModal.css';

export default function AttendanceDatePicker({ customerId, initialDate, actionType, selectedDates, onChange, disabled }) {
  const { t } = useLanguage();
  const [month, setMonth] = useState(initialDate.slice(0, 7));
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const result = response?.month === month && response?.customerId === customerId ? response.data : null;
  const today = result?.today || attendanceToday();

  useEffect(() => {
    let active = true;
    setResponse(null);
    setError(false);
    const [year, number] = month.split('-').map(Number);
    axios.get(`/api/attendance/customers/${customerId}/calendar`, { params: { year, month: number } })
      .then(({ data }) => { if (active) setResponse({ month, customerId, data }); })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [customerId, month, retry]);

  const moveMonth = amount => {
    const date = new Date(`${month}-01T12:00:00Z`);
    date.setUTCMonth(date.getUTCMonth() + amount);
    setMonth(date.toISOString().slice(0, 7));
  };
  const recorded = new Set(result?.recordedDates || result?.presentDates || []);
  // Never offer deletion of an affiliated club's record: manual demarking is owner-scoped.
  const removable = new Set(result?.removableDates || []);
  const selected = new Set(selectedDates);
  const days = result ? calendarDays(month, result.presentDates, today, result.joinedDate) : [];
  const toggleDate = date => onChange(selected.has(date)
    ? selectedDates.filter(value => value !== date) : [...selectedDates, date].sort());
  const selectionsByMonth = selectedDates.reduce((counts, date) => {
    const key = date.slice(0, 7);
    counts[key] = (counts[key] || 0) + 1;
    return counts;
  }, {});

  return <section className="attendance-date-picker" aria-label={t('Select attendance dates')}>
    <p>{t('Click days to select them. Click again to deselect. Selections are kept when you change months.')}</p>
    <div className="attendance-calendar-navigation">
      <button type="button" className="btn btn-secondary" disabled={disabled || month === '0001-01'} onClick={() => moveMonth(-1)} aria-label={t('Previous month')}>←</button>
      <label>{t('Month')} <input type="month" value={month} min="0001-01" max={today.slice(0, 7)} disabled={disabled}
        onChange={event => { if (/^\d{4}-\d{2}$/.test(event.target.value) && event.target.validity.valid) setMonth(event.target.value); }} /></label>
      <button type="button" className="btn btn-secondary" disabled={disabled || month >= today.slice(0, 7)} onClick={() => moveMonth(1)} aria-label={t('Next month')}>→</button>
    </div>
    <div className="attendance-calendar-legend">
      <span className="attendance-day-present">{t('Already marked')}</span>
      <span className="attendance-day-selected">{t('Selected')}</span>
      <span className="attendance-day-future">{t('Unavailable')}</span>
    </div>
    <p className="attendance-calendar-note">{actionType === 'mark'
      ? t('Future dates, dates before registration, and dates already recorded cannot be selected. Program eligibility is checked when you save.')
      : t('Only recorded dates in your own club can be removed. Attendance in affiliated clubs is shown for reference.')}</p>
    {error ? <div role="alert">{t('Could not load attendance.')} <button type="button" className="btn btn-secondary" disabled={disabled} onClick={() => setRetry(value => value + 1)}>{t('Retry')}</button></div>
      : !result ? <p role="status">{t('Loading attendance...')}</p> : <div className="attendance-calendar-grid" role="group" aria-label={t('Attendance dates')}>
        <div className="attendance-calendar-week" aria-hidden="true">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => <div className="attendance-weekday" key={day}>{t(day)}</div>)}
        </div>
        {Array.from({ length: Math.ceil(days.length / 7) }, (_, week) => <div className="attendance-calendar-week" key={week}>
          {Array.from({ length: 7 }, (_, weekday) => {
            const day = days[week * 7 + weekday];
            if (!day) return <div key={weekday} />;
            const unavailable = day.date > today || (actionType === 'mark'
              ? recorded.has(day.date) || day.status === 'Not enrolled' : !removable.has(day.date));
            const isSelected = selected.has(day.date);
            const label = isSelected ? 'Selected' : recorded.has(day.date) ? 'Already marked' : unavailable ? 'Unavailable' : 'Select';
            return <button type="button" key={weekday} aria-label={`${day.date}: ${t(label)}`} aria-pressed={isSelected}
              aria-current={day.date === today ? 'date' : undefined} disabled={disabled || unavailable}
              onClick={() => toggleDate(day.date)}
              className={`attendance-calendar-day attendance-date-option ${isSelected ? 'attendance-day-selected' : recorded.has(day.date) ? 'attendance-day-present' : unavailable ? 'attendance-day-future' : 'attendance-day-available'}`}>
              <strong>{day.day}</strong><span>{t(label)}</span>
            </button>;
          })}
        </div>)}
      </div>}
    <div className="attendance-selection-summary">
      <strong aria-live="polite">{t('Selected Dates ({count})', { count: selectedDates.length })}</strong>
      <button type="button" className="btn btn-secondary" disabled={disabled || !selectedDates.length} onClick={() => onChange([])}>{t('Clear selection')}</button>
    </div>
    {selectedDates.length > 0 && <p className="attendance-calendar-note">{Object.entries(selectionsByMonth).sort().map(([period, count]) => `${period}: ${count}`).join(' · ')}</p>}
  </section>;
}
